import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Search, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface GifPickerProps {
  onGifSelect: (gifUrl: string) => void;
  onClose: () => void;
}

interface GifItem {
  id: string;
  url: string;
  preview: string;
  title: string;
}

export default function GifPicker({ onGifSelect, onClose }: GifPickerProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [gifs, setGifs] = useState<GifItem[]>([]);
  const [trending, setTrending] = useState<GifItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'trending' | 'search'>('trending');
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    fetchTrendingGifs();
  }, []);

  const fetchTrendingGifs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('giphy-search', {
        body: { type: 'trending', limit: 24 }
      });

      if (error) throw error;

      const gifs = data.data.map((gif: any) => ({
        id: gif.id,
        url: gif.images.original.url,
        preview: gif.images.fixed_width_small.url,
        title: gif.title
      }));

      setTrending(gifs);
    } catch (error) {
      console.error('Error fetching trending gifs:', error);
      toast.error('Failed to load trending GIFs');
    } finally {
      setLoading(false);
    }
  };

  const searchGifs = async (query: string) => {
    if (!query.trim()) return;
    
    setLoading(true);
    setHasSearched(true);
    try {
      const { data, error } = await supabase.functions.invoke('giphy-search', {
        body: { type: 'search', q: query, limit: 24 }
      });

      if (error) throw error;

      const gifs = data.data.map((gif: any) => ({
        id: gif.id,
        url: gif.images.original.url,
        preview: gif.images.fixed_width_small.url,
        title: gif.title
      }));

      setGifs(gifs);
      setActiveTab('search');
    } catch (error) {
      console.error('Error searching gifs:', error);
      toast.error('Failed to search GIFs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    searchGifs(searchTerm);
  };

  const currentGifs = activeTab === 'trending' ? trending : gifs;

  return (
    <div className="absolute bottom-full right-0 mb-2 z-50 w-full max-w-sm sm:max-w-md md:w-96 h-[70vh] max-h-96 bg-background border border-border rounded-xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          <span className="font-medium text-sm">GIF</span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="h-6 w-6 p-0 hover:bg-muted rounded-full"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Search */}
      <div className="p-3 border-b border-border">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search for GIFs..."
              className="pl-8 h-8 bg-muted/50 border-muted-foreground/20 focus:bg-background"
            />
          </div>
        </form>
      </div>

      {/* Tabs */}
      <div className="flex px-3 py-2 border-b border-border bg-muted/20">
        <Button
          size="sm"
          variant={activeTab === 'trending' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('trending')}
          className="h-7 text-xs px-3"
        >
          🔥 Trending
        </Button>
        {hasSearched && (
          <Button
            size="sm"
            variant={activeTab === 'search' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('search')}
            className="h-7 text-xs ml-2 px-3"
          >
            🔍 Search ({gifs.length})
          </Button>
        )}
      </div>

      {/* GIF Grid with ScrollArea */}
      <div className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-muted-foreground">Loading GIFs...</span>
            </div>
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {currentGifs.map((gif) => (
                <button
                  key={gif.id}
                  onClick={() => onGifSelect(gif.url)}
                  className="aspect-square bg-muted rounded-lg overflow-hidden hover:ring-2 hover:ring-primary hover:scale-105 transition-all duration-200 group"
                  title={gif.title}
                >
                  <img
                    src={gif.preview}
                    alt={gif.title}
                    className="w-full h-full object-cover group-hover:brightness-110"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
            
            {!loading && currentGifs.length === 0 && (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <div className="text-center">
                  <Zap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {activeTab === 'trending' ? 'No trending GIFs available' : 
                     hasSearched ? 'No GIFs found for your search' : 'Search for GIFs above'}
                  </p>
                  {activeTab === 'search' && hasSearched && (
                    <p className="text-xs mt-1 opacity-70">Try different keywords</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}